import React, { useRef, useEffect } from 'react';
import PhoneInput from 'react-phone-number-input';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';
import './InternationalPhoneInput.scss';

/**
 * Phone input component with country selector and flag
 * Uses react-phone-number-input library for international phone support
 *
 * Preserves the national number when changing countries (e.g., +972555555 → +962555555)
 *
 * @param {object} props - Component props
 * @param {string} props.value - Phone number in E.164 format (e.g., '+972501234567')
 * @param {function} props.onChange - Callback when phone value changes, receives E.164 string
 * @param {string} [props.defaultCountry] - Default country code (e.g., 'IL', 'US')
 * @param {function} [props.onCountryChange] - Callback when country changes, receives country code
 * @param {string} [props.label] - Input label text
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {boolean} [props.required] - Whether the field is required
 * @param {boolean} [props.disabled] - Whether the input is disabled
 * @param {string|boolean} [props.error] - Error message or boolean for error state
 * @param {string} [props.dataTestId] - Test ID for testing
 * @returns {JSX.Element} InternationalPhoneInput component
 */
export const InternationalPhoneInput = ({
    value,
    onChange,
    onCountryChange,
    defaultCountry,
    label,
    placeholder,
    required,
    disabled,
    error,
    dataTestId
}) => {
    const hasError = Boolean(error);

    // Preserves the national number across country changes
    const lastNationalNumberRef = useRef('');
    const isChangingCountryRef = useRef(false);

    // Populate ref when value changes (handles both initial load and user typing)
    useEffect(() => {
        if (value) {
            const parsed = parsePhoneNumberFromString(value);
            if (parsed?.nationalNumber) {
                lastNationalNumberRef.current = parsed.nationalNumber;
            }
        }
    }, [value]);

    const handleChange = (newValue) => {
        // Block library's reset during country change
        if (isChangingCountryRef.current && lastNationalNumberRef.current) {
            return;
        }
        onChange(newValue);
    };

    const handleCountryChange = (newCountry) => {
        isChangingCountryRef.current = true;
        onCountryChange?.(newCountry);

        if (!lastNationalNumberRef.current) {
            isChangingCountryRef.current = false;
            return;
        }

        // Use setTimeout to run AFTER the library's internal onChange
        setTimeout(() => {
            try {
                const reconstructed = parsePhoneNumberFromString(
                    lastNationalNumberRef.current,
                    newCountry
                );

                if (reconstructed) {
                    isChangingCountryRef.current = false;
                    onChange(reconstructed.number);
                }
            } catch {
                isChangingCountryRef.current = false;
            }
        }, 0);
    };

    return (
        <div className={`international-phone-input-wrapper ${hasError ? 'has-error' : ''} ${disabled ? 'is-disabled' : ''}`}>
            {label && (
                <label className="international-phone-input-label">
                    {label}
                    {required && <span className="required-asterisk">*</span>}
                </label>
            )}
            <div className="phone-input-container">
                <PhoneInput
                    international
                    withCountryCallingCode
                    countryCallingCodeEditable={false}
                    defaultCountry={defaultCountry}
                    value={value}
                    onChange={handleChange}
                    onCountryChange={handleCountryChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    data-testid={dataTestId}
                />
            </div>
            {error && typeof error === 'string' && (
                <span className="international-phone-input-error">{error}</span>
            )}
        </div>
    );
};
